'use strict';

import AppDispatcher from '../AppDispatcher';
import ActionTypes from '../constants/ActionTypes';
import EmailFlags from '../constants/EmailFlags';
import invariant from 'react/lib/invariant';
import update from 'react/lib/update';
import { createStore, mergeIntoBag, isInBag } from '../utils/StoreUtils';
import StoreUtils from '../utils/StoreUtils';
import values from 'lodash/object/values';

const _users = {};
let _currentUserId;
let _loginEndpoints;
let _signupEndpoints;

function toggleEmailNotificationFlag(flag, isEnabled) {
  const isDisabled = !isEnabled;
  /*jshint bitwise:false*/
  _users[_currentUserId] = update(_users[_currentUserId], {
    emailFlags: {
      $apply: flags => (isDisabled ? flags | flag : flags & ~flag)
    }
  });
}

const UserStore = StoreUtils.createStore({
  contains(id, fields) {
    return isInBag(_users, id, fields);
  },

  get(id) {
    return _users[id];
  },

  getMe() {
    return UserStore.get(_currentUserId);
  },

  isMe(userOrId) {
    const me = this.getMe();
    if (me) {
      return me === userOrId || me.id === userOrId;
    } else {
      return false;
    }
  },

  isAuthenticated() {
    return !!this.getMe();
  },

  isEmailNotificationEnabled(flag) {
    invariant(values(EmailFlags).indexOf(flag) > -1, 'Expected one of EmailFlags, got %s', flag);

    /*jshint bitwise:false*/
    const { emailFlags } = this.getMe();
    return !(emailFlags & flag);
  },

  getLoginEndpoints() {
    return _loginEndpoints;
  },

  getSignupEndpoints() {
    return _signupEndpoints;
  }
});

UserStore.dispatchToken = AppDispatcher.register(function (payload) {
  const action = payload.action;
  const userId = action.userId;
  const response = action.response;
  const entities = response && response.entities;
  const receivedUsers = entities && entities.users;

  switch (action.type) {
  case ActionTypes.SPECIFY_CURRENT_USER:
    _currentUserId = action.userId;
    break;

  case ActionTypes.SPECIFY_LOGIN_OPTIONS:
    _loginEndpoints = action.loginEndpoints;
    _signupEndpoints = action.signupEndpoints;
    break;

  case ActionTypes.ENABLE_EMAIL_NOTIFICATION:
    toggleEmailNotificationFlag(action.flag, true);
    break;

  case ActionTypes.DISABLE_EMAIL_NOTIFICATION:
    toggleEmailNotificationFlag(action.flag, false);
    break;

  case ActionTypes.FOLLOW_USER:
  case ActionTypes.UNFOLLOW_USER_ERROR:
    _users[userId] = update(_users[userId], {
      isSubscribed: {
        $set: true
      },
      subscriberCount: {
        $apply: subscriberCount => subscriberCount + 1
      }
    });
    _users[_currentUserId] = update(_users[_currentUserId], {
      subscriptionCount: {
        $apply: subscriptionCount => subscriptionCount + 1
      }
    });
    UserStore.emitChange();
    break;

  case ActionTypes.UNFOLLOW_USER:
  case ActionTypes.FOLLOW_USER_ERROR:
    _users[userId] = update(_users[userId], {
      isSubscribed: {
        $set: false
      },
      subscriberCount: {
        $apply: subscriberCount => subscriberCount - 1
      }
    });
    _users[_currentUserId] = update(_users[_currentUserId], {
      subscriptionCount: {
        $apply: subscriptionCount => subscriptionCount - 1
      }
    });
    UserStore.emitChange();
    break;

  case ActionTypes.POST_STAMP_SUCCESS:
  case ActionTypes.DELETE_DRAFT_SUCCESS:
    _users[_currentUserId] = update(_users[_currentUserId], {
      draftCount: {
        $apply: draftCount => draftCount - 1
      }
    });
    UserStore.emitChange();
    break;

  case ActionTypes.SAVE_DRAFT_SUCCESS:
    if (action.isNewDraft) {
      _users[_currentUserId] = update(_users[_currentUserId], {
        draftCount: {
          $apply: draftCount => draftCount + 1
        }
      });
      UserStore.emitChange();
    }
    break;

  case ActionTypes.LOGIN_WITH_SERVICE_ACCOUNT_SUCCESS:
  case ActionTypes.SIGNUP_WITH_SERVICE_ACCOUNT_SUCCESS:
  case ActionTypes.LOGIN_WITH_EMAIL_SUCCESS:
    _currentUserId = response.result;
    UserStore.emitChange();
    break;
  }

  if (receivedUsers) {
    mergeIntoBag(_users, receivedUsers);
    UserStore.emitChange();
  }
});

export default UserStore;



/** WEBPACK FOOTER **
 ** ./stores/UserStore.js
 **/